import { memo, useEffect, useState } from 'react';
import type {
  CSSProperties,
  MouseEventHandler,
  ReactEventHandler,
  SyntheticEvent
} from 'react';
import type { IconType } from '@root/src/types';
import './index.scss';
import { toWebpSrcSet } from './webp';

// The multi-resolution variant of IconType (the `{ x1, x2, x3 }` object).
type IconSrcSet = Extract<IconType, { x2: unknown }>;

interface PictureProps {
  icon?: IconType | null;
  iconHover?: IconType | null;
  iconDisabled?: IconType | null;
  isHovered?: boolean;
  isDisabled?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  pictureClassName?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLElement>;
  onMouseEnter?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onMouseLeave?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: ReactEventHandler<HTMLImageElement>;
  alt?: string;
  loading?: 'eager' | 'lazy';
}

const isSrcSet = (value: IconType | null | undefined): value is IconSrcSet =>
  typeof value === 'object' && value !== null && 'x2' in value;

const srcSetOf = (icon: IconSrcSet): string =>
  `${icon.x2}, ${icon.x3}, ${icon.x3}`;

const SIZES =
  '(max-width: 3440px), (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)';

const Picture = ({
  icon,
  iconHover,
  isHovered,
  height,
  width,
  className,
  onClick,
  style,
  pictureClassName,
  onMouseEnter,
  onMouseLeave,
  iconDisabled,
  isDisabled,
  onError,
  alt = 'Изображение',
  loading = 'lazy'
}: PictureProps) => {
  const defaultValue: string | false =
    typeof icon === 'string'
      ? isDisabled && typeof iconDisabled === 'string'
        ? iconDisabled
        : isHovered && typeof iconHover === 'string'
        ? iconHover
        : icon
      : isSrcSet(icon)
      ? isDisabled && isSrcSet(iconDisabled)
        ? srcSetOf(iconDisabled)
        : isHovered && isSrcSet(iconHover)
        ? srcSetOf(iconHover)
        : srcSetOf(icon)
      : false;

  const [value, setValue] = useState(defaultValue);
  useEffect(() => {
    setValue(defaultValue);
  }, [icon, iconHover, isHovered, iconDisabled, isDisabled, defaultValue]);

  const onMouseEnterFunc = (e: SyntheticEvent<HTMLImageElement>): void => {
    if (typeof icon === 'string') {
      if (typeof iconHover === 'string' && !isDisabled) {
        setValue(iconHover);
      }
    } else if (isSrcSet(icon)) {
      if (isSrcSet(iconHover) && !isDisabled) {
        setValue(srcSetOf(iconHover));
      }
    }

    onMouseEnter?.(e);
  };
  const onMouseLeaveFunc = (e: SyntheticEvent<HTMLImageElement>): void => {
    if (typeof icon === 'string') {
      if (icon && !isHovered && !isDisabled) {
        setValue(icon);
      }
    } else if (isSrcSet(icon)) {
      if (!isHovered && !isDisabled) {
        setValue(srcSetOf(icon));
      }
    }

    onMouseLeave?.(e);
  };
  if (icon === null || icon === undefined) {
    return null;
  }

  if (typeof icon === 'string') {
    const webp = toWebpSrcSet(value);
    const img = (
      <img
        onError={onError || undefined}
        draggable='false'
        className={className || ''}
        style={style}
        onMouseEnter={onMouseEnterFunc}
        onMouseLeave={onMouseLeaveFunc}
        onFocus={onMouseLeaveFunc}
        src={value || undefined}
        width={width === undefined || width === null ? undefined : `${width}`}
        height={height === undefined || height === null ? undefined : `${height}`}
        alt={alt}
        loading={loading}
      />
    );
    return (
      <picture
        className={`picture-component${
          onClick ? ' picture-component--on-click' : ''
        } ${pictureClassName || ''}`}
        onClick={onClick}
      >
        {webp && <source type='image/webp' srcSet={webp} sizes={SIZES} />}
        {img}
      </picture>
    );
  }
  if (isSrcSet(icon)) {
    const webp = toWebpSrcSet(value);
    return (
      <picture
        className={`picture-component${
          onClick ? ' picture-component--on-click' : ''
        } ${pictureClassName || ''}`}
        onClick={onClick}
      >
        {webp && <source type='image/webp' srcSet={webp} sizes={SIZES} />}
        <img
          onError={onError || undefined}
          draggable='false'
          className={className}
          width={width === undefined || width === null ? undefined : `${width}`}
          height={height === undefined || height === null ? undefined : `${height}`}
          style={style}
          onMouseEnter={onMouseEnterFunc}
          onMouseLeave={onMouseLeaveFunc}
          alt={alt}
          srcSet={value || undefined}
          sizes={SIZES}
          loading={loading}
        />
      </picture>
    );
  }
  return null;
};

export default memo(Picture);
